import { CreateFolderRequest, CreateFolderResponse, GetFolderResponse, GetTopicResponse } from '../../types';
import { buildQueryString, del, emptyPost, get, logWithAmplitude, post, put, throwHttpError } from './api';

import { getFromLocalStorage } from '../utils';

export async function createFolder(
    amplitude_callsite: string,
    folder: CreateFolderRequest,
    additional_logged_parameters: Object = {}
) {
    const organization_id = getFromLocalStorage('organization_id');
    if (organization_id !== null) {
        folder.organization_id = { $oid: organization_id };
    }

    const folder_response = await addFolderInner(folder);
    logWithAmplitude(
        amplitude_callsite,
        'Folder Created',
        Object.assign(
            {},
            { folder_id: folder_response._id, folder_name: folder.name },
            additional_logged_parameters
        )
    );
    return folder_response;
}

async function addFolderInner(folder: CreateFolderRequest) {
    const response = await fetch(`/api/folders`, post(folder));
    if (!response.ok) {
        await throwHttpError('An error occurred while creating the folder.', response);
    }

    return await (response.json() as Promise<CreateFolderResponse>);
}

export async function getFolders(amplitude_callsite: string) {
    const organization_id = getFromLocalStorage('organization_id');
    const queryString = buildQueryString({ organization_id });
    const response = await fetch(`/api/folders${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the folders.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get All Folders for account');
    return await (response.json() as Promise<GetFolderResponse[]>);
}

export async function getFolder(
    amplitude_callsite: string,
    folder_id: string,
    use_cache?: boolean
) {
    const queryString = buildQueryString({ use_cache });
    const response = await fetch(`/api/folders/${folder_id}${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the folder.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Folder', {
        folder_id,
        use_cache,
    });
    return await (response.json() as Promise<GetFolderResponse>);
}

export async function getTopicsForFolder(
    amplitude_callsite: string,
    folder_id: string,
    use_cache?: boolean
) {
    const queryString = buildQueryString({ use_cache });

    const response = await fetch(`/api/folders/${folder_id}/topics${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the topics.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Topics', {
        folder_id,
        use_cache,
    });
    return await (response.json() as Promise<GetTopicResponse[]>);
}

export async function removeFolder(amplitude_callsite: string, folder_id: string) {
    const response = await fetch(`/api/folders/${folder_id}`, del());

    if (!response.ok) {
        await throwHttpError('An error occurred while deleting the folder.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Delete Folder', {
        folder_id,
    });
}

export async function updateFolder(
    amplitude_callsite: string,
    folder_id: string,
    folder: CreateFolderRequest,
    additional_logged_parameters: Object = {}
) {
    const response = await fetch(`/api/folders/${folder_id}`, put(folder));
    if (!response.ok) {
        await throwHttpError('An error occurred while updating the folder.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'Folder Updated',
        Object.assign({}, { folder_id }, additional_logged_parameters)
    );
}

export async function addTopicToFolder(
    amplitude_callsite: string,
    folder_id: string,
    topic_id: string,
    additional_logged_parameters: Object = {}
) {
    const response = await fetch(`/api/folders/${folder_id}/topics/${topic_id}`, (emptyPost()));
    if (!response.ok) {
        await throwHttpError('An error occurred while adding the topic the folder.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'Topic Added To Folder',
        Object.assign({}, { folder_id, topic_id }, additional_logged_parameters)
    );
}

export async function removeTopicFromFolder(
    amplitude_callsite: string,
    folder_id: string,
    topic_id: string,
    additional_logged_parameters: Object = {}
) {
    const response = await fetch(`/api/folders/${folder_id}/topics/${topic_id}`, del());
    if (!response.ok) {
        await throwHttpError('An error occurred while removing the topic from folder.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'Topic Removed From Folder',
        Object.assign({}, { folder_id, topic_id }, additional_logged_parameters)
    );
}
